import { useEffect, useRef, useState } from "react";
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
} from "@floating-ui/react";
import type { CSSProperties, ReactNode } from "react";
import type { KeywordSnapshot } from "../api/types";
import type { Theme } from "../theme/pixel";
import { keywordSummary } from "../data/keywords";

export function KeywordIconStrip({
  keywords,
  theme,
  // Icons are 32×32; default to 1:1 so they render crisp (integer scale).
  size = 32,
  summoningSickness = false,
}: {
  keywords: KeywordSnapshot[];
  theme: Theme;
  size?: number;
  /** When true, renders the summoning-sickness icon first. Sickness
   *  isn't a real Keyword — it's a per-turn state — but it shares the
   *  same slot in the strip so the player has one place to look. */
  summoningSickness?: boolean;
}) {
  if (keywords.length === 0 && !summoningSickness) return null;
  return (
    <div
      // Marker for click-outside-cards so dragging onto the strip
      // doesn't clear the inspect state.
      data-card-tile="true"
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        gap: 3,
        marginTop: 4,
        maxWidth: "100%",
      }}
    >
      {summoningSickness && (
        <IconWithTooltip
          theme={theme}
          size={size}
          src="/keyword-icons/summoning_sickness.png"
          label="Summoning Sickness"
          summary="This creature entered the battlefield this turn. It can't attack until next turn (unless it has Rush)."
        />
      )}
      {keywords.map((kw) => (
        <KeywordIcon key={kw.name} kw={kw} theme={theme} size={size} />
      ))}
    </div>
  );
}

function KeywordIcon({
  kw,
  theme,
  size,
}: {
  kw: KeywordSnapshot;
  theme: Theme;
  size: number;
}) {
  const summary = keywordSummary(kw.name);
  const label = kw.x !== null ? `${kw.name} ${kw.x}` : kw.name;
  return (
    <IconWithTooltip
      theme={theme}
      size={size}
      src={`/keyword-icons/${kw.name}.png`}
      label={label}
      summary={summary}
      badge={
        kw.x !== null ? (
          <span
            style={{
              position: "absolute",
              right: -2,
              bottom: -3,
              padding: "0 3px",
              background: theme.panelEdgeStrong,
              color: theme.panelSolid,
              fontFamily: theme.fontDisplay,
              fontSize: Math.max(7, Math.round(size * 0.42)),
              lineHeight: 1,
              border: `1px solid ${theme.bgDeep}`,
            }}
          >
            {kw.x}
          </span>
        ) : null
      }
    />
  );
}

// Shared icon + Floating UI tooltip used by every entry in the strip
// (real keywords and the synthetic Summoning Sickness state). Keeps
// the hover/focus/dismiss behaviour consistent across every icon.
function IconWithTooltip({
  theme,
  size,
  src,
  label,
  summary,
  badge,
}: {
  theme: Theme;
  size: number;
  src: string;
  label: string;
  summary: string | null | undefined;
  badge?: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "top",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // The tooltip is a pure function of "is the cursor geometrically
  // over the icon RIGHT NOW." Floating-ui's `useHover` relies on
  // `pointerenter`/`pointerleave`, which the browser synthesises on
  // elements that get `visibility: hidden` mid-frame — exactly what
  // the View Transitions API does to every named element for the
  // duration of the transition. That synthetic `pointerleave` closed
  // the tooltip every time the opponent took an action, even though
  // the cursor never moved. To avoid that, we open on `mouseenter`
  // (the trigger event) and only close when a real `pointermove`
  // confirms the cursor is geometrically OUTSIDE the icon's current
  // bounding rect. `pointermove` fires on the user's actual cursor
  // motion, never as a side effect of a VT, so the close path is
  // immune to animation noise. Result: the tooltip opens the
  // instant the cursor lands and closes only when it leaves.
  const iconRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const el = iconRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Skip when the element has no layout (jsdom test environment,
      // or pre-mount): we can't make a geometric judgement and
      // shouldn't clobber the `mouseenter`-driven open state.
      if (r.width === 0 && r.height === 0) return;
      const inside =
        e.clientX >= r.left &&
        e.clientX <= r.right &&
        e.clientY >= r.top &&
        e.clientY <= r.bottom;
      setOpen((cur) => (cur === inside ? cur : inside));
    };
    window.addEventListener("pointermove", onMove);
    return () => {
      window.removeEventListener("pointermove", onMove);
    };
  }, []);

  // Bind `mouseenter` as a NATIVE listener (not React's synthetic
  // onMouseEnter). React synthesises onMouseEnter by listening to
  // `mouseover` and inspecting `relatedTarget`, which means a unit
  // test that calls `dispatchEvent(new MouseEvent("mouseenter"))`
  // would not trigger the React handler. The geometric-poll above
  // is the primary signal in real use; this listener exists so the
  // tooltip opens the instant the cursor lands without waiting for
  // the next `pointermove` tick, AND so the existing test that
  // fires `mouseenter` directly continues to pass.
  useEffect(() => {
    const el = iconRef.current;
    if (!el) return;
    const onEnter = () => setOpen(true);
    el.addEventListener("mouseenter", onEnter);
    return () => {
      el.removeEventListener("mouseenter", onEnter);
    };
  }, []);

  const bindRef = (node: HTMLSpanElement | null) => {
    iconRef.current = node;
    refs.setReference(node);
  };

  const iconStyle: CSSProperties = {
    width: size,
    height: size,
    imageRendering: "pixelated",
    display: "block",
  };

  const tooltipStyle: CSSProperties = {
    ...floatingStyles,
    background: theme.bgDeep,
    color: theme.ink,
    border: `2px solid ${theme.panelEdgeStrong}`,
    boxShadow: `4px 4px 0 0 rgba(0,0,0,0.4)`,
    padding: "8px 10px",
    fontFamily: theme.fontBody,
    fontSize: 12,
    lineHeight: 1.4,
    maxWidth: 280,
    zIndex: 2000,
    imageRendering: "pixelated",
    pointerEvents: "auto",
    fontWeight: 400,
  };

  const headingStyle: CSSProperties = {
    fontFamily: theme.fontDisplay,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: theme.letterSpacing,
    color: theme.accent,
    textTransform: theme.uppercase ? "uppercase" : "none",
    marginBottom: 4,
  };

  return (
    <>
      <span
        ref={bindRef}
        tabIndex={0}
        aria-label={label}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        style={{
          position: "relative",
          display: "inline-block",
          lineHeight: 0,
          border: `1px solid ${theme.panelEdge}`,
          background: theme.panelSolid,
        }}
      >
        <img src={src} alt="" draggable={false} style={iconStyle} />
        {badge}
      </span>
      {open && summary && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            role="tooltip"
            style={tooltipStyle}
          >
            <div style={headingStyle}>{label}</div>
            <div>{summary}</div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
